import React, { useState, useEffect, useRef } from 'react';
import { theme } from '../config/theme';
import { autocompleteAddress, lookupAddress } from '../services/api';

interface Address {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
}

interface AddressFieldsProps {
  value: Address;
  onChange: (address: Address) => void;
  required?: boolean;
}

const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' }
];

const AddressFields: React.FC<AddressFieldsProps> = ({ value, onChange, required = false }) => {
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const autocompleteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchAddress = async (query: string) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    try {
      const data = await autocompleteAddress(query, 10);
      
      // Sort results to prioritize Atlanta and GA
      const sortedItems = (data.items || []).sort((a: any, b: any) => {
        const aIsAtlanta = a.address?.city?.toLowerCase().includes('atlanta');
        const bIsAtlanta = b.address?.city?.toLowerCase().includes('atlanta');
        const aIsGA = a.address?.stateCode === 'GA' || a.address?.state === 'Georgia';
        const bIsGA = b.address?.stateCode === 'GA' || b.address?.state === 'Georgia';
        
        // Prioritize: Atlanta > GA > Other
        if (aIsAtlanta && !bIsAtlanta) return -1;
        if (!aIsAtlanta && bIsAtlanta) return 1;
        if (aIsGA && !bIsGA) return -1;
        if (!aIsGA && bIsGA) return 1;
        return 0;
      });
      
      setSuggestions(sortedItems.slice(0, 5)); // Limit to 5 after sorting
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    searchAddress(query);
  };

  const selectSuggestion = async (item: any) => {
    try {
      // Get detailed address information from backend
      const data = await lookupAddress(item.id);
      const addr = data.address;

      // Parse and populate address fields
      const newAddress: Address = {
        line1: addr.houseNumber && addr.street ? `${addr.houseNumber} ${addr.street}` : addr.street || addr.label || '',
        line2: '',
        city: addr.city || '',
        state: addr.stateCode || addr.state || '',
        postal_code: addr.postalCode ? addr.postalCode.split('-')[0] : ''
      };

      onChange(newAddress);
      setSearchQuery('');
      setShowSuggestions(false);
      setSuggestions([]);
    } catch (error) {
      console.error('Error fetching address details:', error);
    }
  };

  const handleFieldChange = (field: keyof Address, fieldValue: string) => {
    onChange({
      ...value,
      [field]: fieldValue
    });
  };

  return (
    <div className="space-y-4">
      {/* Address Autocomplete Search */}
      <div className="relative" ref={autocompleteRef}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Search Address
        </label>
        <input
          type="text"
          autoComplete="off"
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Start typing an address..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
          style={{ '--tw-ring-color': theme.colors.primary[500] } as React.CSSProperties}
        />
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            {suggestions.map((item, index) => {
              // Format address as: line1, line2, city, state, zip
              const formatAddress = () => {
                const parts = [];
                const addr = item.address;
                
                // Line 1 (street address)
                if (addr?.houseNumber && addr?.street) {
                  parts.push(`${addr.houseNumber} ${addr.street}`);
                } else if (addr?.street) {
                  parts.push(addr.street);
                }
                
                // City
                if (addr?.city) parts.push(addr.city);
                
                // State
                if (addr?.stateCode) {
                  parts.push(addr.stateCode);
                } else if (addr?.state) {
                  parts.push(addr.state);
                }
                
                // Postal code (without extended zip)
                if (addr?.postalCode) {
                  const zip = addr.postalCode.split('-')[0];
                  parts.push(zip);
                }
                
                return parts.join(', ');
              };
              
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => selectSuggestion(item)}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-b-0"
                >
                  <div className="text-sm font-medium text-gray-900">{formatAddress()}</div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Address Line 1 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Address Line 1 {required && '*'}
        </label>
        <input
          type="text"
          required={required}
          value={value.line1}
          onChange={(e) => handleFieldChange('line1', e.target.value)}
          placeholder="Street address"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
          style={{ '--tw-ring-color': theme.colors.primary[500] } as React.CSSProperties}
        />
      </div>

      {/* Address Line 2 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Address Line 2
        </label>
        <input
          type="text"
          value={value.line2}
          onChange={(e) => handleFieldChange('line2', e.target.value)}
          placeholder="Apartment, suite, etc. (optional)"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
          style={{ '--tw-ring-color': theme.colors.primary[500] } as React.CSSProperties}
        />
      </div>

      {/* City, State, Zip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            City {required && '*'}
          </label>
          <input
            type="text"
            required={required}
            value={value.city}
            onChange={(e) => handleFieldChange('city', e.target.value)}
            placeholder="City"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
            style={{ '--tw-ring-color': theme.colors.primary[500] } as React.CSSProperties}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            State {required && '*'}
          </label>
          <select
            required={required}
            value={value.state}
            onChange={(e) => handleFieldChange('state', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
            style={{ '--tw-ring-color': theme.colors.primary[500] } as React.CSSProperties}
          >
            <option value="">Select State</option>
            {US_STATES.map((state) => (
              <option key={state.code} value={state.code}>
                {state.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            ZIP Code {required && '*'}
          </label>
          <input
            type="text"
            required={required}
            value={value.postal_code}
            onChange={(e) => handleFieldChange('postal_code', e.target.value)}
            placeholder="ZIP"
            maxLength={5}
            pattern="[0-9]{5}"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2"
            style={{ '--tw-ring-color': theme.colors.primary[500] } as React.CSSProperties}
          />
        </div>
      </div>
    </div>
  );
};

export default AddressFields;
